"use client";

import { CountryInfo } from "@/store/types/countryTypes";
import React from "react";
import { Paperclip, Clock } from "lucide-react";

const CountryStudentVisaProcess: React.FC<{ country: CountryInfo }> = ({ country }) => {
  const sectionData = country?.studentVisaProcess;

  const steps = sectionData?.steps || [];

  if (steps.length === 0) {
    return null;
  }

  const heading = sectionData?.heading || `${country.countryName} Student Visa`;
  const subheading = sectionData?.subheading;

  const totalTime = sectionData?.totalTime;
  const totalCost = sectionData?.totalCost;
  const successRate = sectionData?.successRate;

  return (
    <div className="w-full my-12 space-y-8">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-[#1C2233] ff_poppins">
          {heading}
        </h2>
        {subheading && (
          <p className="mt-1.5 text-xs sm:text-sm text-[#667085] ff_poppins">
            {subheading}
          </p>
        )}
      </div>

      {/* STATS */}
      {(totalTime || totalCost || successRate) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {totalTime && (
            <div className="bg-white rounded-[18px] border border-[#E0D8FF] p-6 shadow-sm">
              <p className="text-xs uppercase font-bold text-[#808A9D] tracking-wider ff_poppins">
                Total Time
              </p>
              <p className="mt-2 text-2xl font-extrabold text-[#2B7FFF] ff_poppins">
                {totalTime}
              </p>
            </div>
          )}

          {totalCost && (
            <div className="bg-white rounded-[18px] border border-[#E0D8FF] p-6 shadow-sm">
              <p className="text-xs uppercase font-bold text-[#808A9D] tracking-wider ff_poppins">
                Total Cost
              </p>
              <p className="mt-2 text-2xl font-extrabold text-[#2B7FFF] ff_poppins">
                {totalCost}
              </p>
            </div>
          )}

          {successRate && (
            <div className="bg-white rounded-[18px] border border-[#E0D8FF] p-6 shadow-sm">
              <p className="text-xs uppercase font-bold text-[#808A9D] tracking-wider ff_poppins">
                Success Rate
              </p>
              <p className="mt-2 text-2xl font-extrabold text-[#2B7FFF] ff_poppins">
                {successRate}
              </p>
            </div>
          )}
        </div>
      )}

      {/* STEP BY STEP CARDS */}
      <div className="space-y-4">
        {steps.map((step, idx) => {
          const isFirst = idx === 0;
          return (
            <div
              key={idx}
              className="bg-white rounded-[18px] border border-[#E0D8FF] p-5 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                {/* Step Circle */}
                <div
                  className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-white text-base shrink-0 ${
                    isFirst ? "bg-[#2B7FFF]" : "bg-[#808A9D]"
                  }`}
                >
                  {step.stepNumber || idx + 1}
                </div>

                <div className="space-y-2">
                  <h3 className="text-base sm:text-lg font-bold text-[#1C2233] ff_poppins">
                    {step.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#525B73] ff_poppins leading-relaxed">
                    {step.description}
                  </p>

                  {/* Document Tag Pills */}
                  {step.tags && step.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {step.tags.map((tag, tIdx) => (
                        <span
                          key={tIdx}
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFF5F2] border border-[#FFDCD2] text-xs font-semibold text-[#FF5B29] ff_poppins"
                        >
                          <Paperclip className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Time Badge */}
              {step.timeBadge && (
                <div className="self-end md:self-center shrink-0">
                  <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#FFF3EA] text-[#FF852D] font-bold text-xs sm:text-sm ff_poppins border border-[#FFE1CD]">
                    <Clock className="w-3.5 h-3.5" />
                    {step.timeBadge}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CountryStudentVisaProcess;

